"use client";
import { useCallback, useEffect } from "react";
import { usePathname } from "next/navigation";
import useIsClient from "@/hooks/useIsClient";
import useShouldFire from "./useShouldFire";

/**
 * PERF: this used to `import SmartlookClient from "smartlook-client"` at module
 * scope, which put the SDK (~40KB built) in the every-page layout chunk — reached
 * via `PageViewPixel` -> `layout.tsx` — even though the recorder is idle-gated,
 * setting-gated, and skipped entirely on low-end devices.
 *
 * The package was never doing any work for us: the recorder is installed by the
 * inline snippet in `PixelsHeadScripts`, and the two methods used here are thin
 * wrappers over the global it defines. From `smartlook-client/dist/index.js`:
 *
 *   navigation: (path) => { if (!window.smartlook) return false;
 *                           window.smartlook('navigation', path); return true; }
 *   track: (name, props) => { ... window.smartlook('track', name, props); ... }
 *
 * So we call the global directly and the dependency is gone. The `?.` guard
 * replaces the package's `console.warn` when the recorder is absent — which is the
 * normal case on low-end devices, where the snippet is deliberately not injected.
 */
type SmartlookGlobal = ((...args: unknown[]) => void) | undefined;
const smartlook = (): SmartlookGlobal =>
  typeof window === "undefined"
    ? undefined
    : (window as unknown as { smartlook?: SmartlookGlobal }).smartlook;

function useSmartlook(smartlook_code: string, refresh?: boolean) {
  const pathname = usePathname();
  const client = useIsClient();
  const shouldFire = useShouldFire();

  useEffect(() => {
    if (!client || !smartlook_code || !shouldFire) return;
    if (pathname) {
      // Set the user's current page URL as a user property
      smartlook()?.("navigation", pathname);
    }
  }, [pathname, client, smartlook_code, refresh, shouldFire]);

  const trackEvent = useCallback(
    (
      eventName: string,
      props: {
        [key: string]: string | number | boolean;
      },
    ) => {
      smartlook()?.("track", eventName, props);
    },
    [],
  );

  return { trackEvent };
}

export default useSmartlook;
